import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Send, Check } from 'lucide-react';
import axios from 'axios';
import { NIMNY_AVATAR, translations } from '../data/translations';
import { useLanguage } from '../context/LanguageContext';
import { NightAmbience } from '../components/NimnyLanding';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Detect language from URL path
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/ru/')) {
      setLanguage('ru');
    } else if (path.startsWith('/es/')) {
      setLanguage('es');
    }
  }, [location.pathname, setLanguage]);

  // Add canonical tag, hreflang, and BreadcrumbList schema
  useEffect(() => {
    const langCode = language === 'ru' ? 'ru' : language === 'es' ? 'es' : 'en';
    const canonicalUrl = language === 'en' ? 'https://readnimny.com/contact' : `https://readnimny.com/${langCode}/contact`;
    const homeUrl = 'https://readnimny.com/';
    
    const homeName = language === 'ru' ? 'Главная' : language === 'es' ? 'Inicio' : 'Home';
    const contactName = language === 'ru' ? 'Контакты' : language === 'es' ? 'Contacto' : 'Contact';
    
    // Update page title
    document.title = t(translations.contactPage.metaTitle);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    // Hreflang tags
    const hreflangData = [
      { lang: 'en', href: 'https://readnimny.com/contact' },
      { lang: 'es', href: 'https://readnimny.com/es/contact' },
      { lang: 'ru', href: 'https://readnimny.com/ru/contact' },
      { lang: 'x-default', href: 'https://readnimny.com/contact' }
    ];

    // Remove existing hreflang tags
    document.querySelectorAll('link[hreflang]').forEach(el => el.remove());

    // Add new hreflang tags
    hreflangData.forEach(({ lang, href }) => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = lang;
      link.href = href;
      document.head.appendChild(link);
    });

    // BreadcrumbList Schema: Home → Contact
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": homeName,
          "item": homeUrl
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": contactName,
          "item": canonicalUrl
        }
      ]
    };

    // Remove existing breadcrumb schema if present
    const existingBreadcrumbSchema = document.getElementById('breadcrumb-schema');
    if (existingBreadcrumbSchema) {
      existingBreadcrumbSchema.remove();
    }

    // Add BreadcrumbList schema
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.id = 'breadcrumb-schema';
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    return () => {
      document.querySelectorAll('link[hreflang]').forEach(el => el.remove());
      const breadcrumbEl = document.getElementById('breadcrumb-schema');
      if (breadcrumbEl) {
        breadcrumbEl.remove();
      }
    };
  }, [language, t]);

  // FAQ structured data (FAQPage schema) — moved from About page so the
  // App Store Support URL exposes the FAQ to crawlers.
  useEffect(() => {
    const faqStructuredData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": translations.aboutPage.faq.map(item => ({
        "@type": "Question",
        "name": item.question[language] || item.question['en'],
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer[language] || item.answer['en']
        }
      }))
    };

    const existing = document.getElementById('faq-schema');
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.id = 'faq-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(faqStructuredData);
    document.head.appendChild(script);

    return () => {
      const schema = document.getElementById('faq-schema');
      if (schema) schema.remove();
    };
  }, [language]);

  // Get localized home URL
  const getHomeUrl = () => {
    if (language === 'ru') return '/';
    if (language === 'es') return '/';
    return '/';
  };

  // Get localized contact link for Privacy page
  const getContactUrl = () => {
    if (language === 'ru') return '/ru/contact';
    if (language === 'es') return '/es/contact';
    return '/contact';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.message) {
      setError(t(translations.contactPage.errorRequired));
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      setError(t(translations.contactPage.errorGeneric));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative">
      {/* Night sky background — consistent with the rest of the site */}
      <NightAmbience />

      {/* Header */}
      <header className="relative z-10 py-6 px-6">
        <div className="max-w-3xl mx-auto">
          <Link 
            to={getHomeUrl()} 
            className="inline-flex items-center gap-2 text-amber-200/70 hover:text-amber-200 transition-colors font-sans"
            data-testid="contact-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            {t(translations.contactPage.backToHome)}
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="relative z-10 px-6 pb-20">
        <div className="max-w-xl mx-auto">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-10">
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-amber-200/30">
              <img 
                src={NIMNY_AVATAR}
                alt="Nimny"
                className="w-full h-full object-cover"
                style={{ transform: 'scale(1.2)', objectPosition: 'center 35%' }}
              />
            </div>
            <span className="font-sans text-2xl text-[#f5ebe0]/90">Nimny</span>
          </div>

          {/* Title */}
          <h1 className="font-sans text-3xl md:text-4xl text-[#f5ebe0]/90 text-center mb-3" data-testid="contact-title">
            {t(translations.contactPage.title)}
          </h1>
          <p className="font-serif text-base md:text-lg text-amber-200/60 text-center mb-10" data-testid="contact-subtitle">
            {t(translations.contactPage.subtitle)}
          </p>

          {/* Form Card */}
          <div className="bg-[#f5ebe0]/5 backdrop-blur-sm rounded-2xl p-8 md:p-10 border border-amber-200/15">
            {isSubmitted ? (
              <div className="text-center py-8" data-testid="contact-success">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#c9a87c]/10 flex items-center justify-center">
                  <Check className="w-8 h-8 text-[#c9a87c]" />
                </div>
                <h2 className="font-sans text-xl text-[#f5ebe0]/90 mb-3">
                  {t(translations.contactPage.thankYou)}
                </h2>
                <p className="font-serif text-amber-200/60">
                  {t(translations.contactPage.thankYouMessage)}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name (optional) */}
                <div>
                  <label className="block font-sans text-sm text-amber-200/60 mb-2">
                    {t(translations.contactPage.nameLabel)} <span className="text-amber-200/30">{t(translations.contactPage.optional)}</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t(translations.contactPage.namePlaceholder)}
                    className="w-full px-4 py-3 rounded-xl bg-[#2a2a45] border border-amber-200/20 focus:border-amber-200/40 focus:outline-none font-sans text-[#f5ebe0] placeholder:text-[#f5ebe0]/30 transition-colors"
                    data-testid="contact-name-input"
                  />
                </div>

                {/* Email (required) */}
                <div>
                  <label className="block font-sans text-sm text-amber-200/60 mb-2">
                    {t(translations.contactPage.emailLabel)} <span className="text-[#c9a87c]">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder={t(translations.contactPage.emailPlaceholder)}
                    className="w-full px-4 py-3 rounded-xl bg-[#2a2a45] border border-amber-200/20 focus:border-amber-200/40 focus:outline-none font-sans text-[#f5ebe0] placeholder:text-[#f5ebe0]/30 transition-colors"
                    data-testid="contact-email-input"
                  />
                </div>

                {/* Message (required) */}
                <div>
                  <label className="block font-sans text-sm text-amber-200/60 mb-2">
                    {t(translations.contactPage.messageLabel)} <span className="text-[#c9a87c]">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder={t(translations.contactPage.messagePlaceholder)}
                    className="w-full px-4 py-3 rounded-xl bg-[#2a2a45] border border-amber-200/20 focus:border-amber-200/40 focus:outline-none font-sans text-[#f5ebe0] placeholder:text-[#f5ebe0]/30 transition-colors resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                {/* Error message */}
                {error && (
                  <p className="text-red-400 text-sm font-sans text-center" data-testid="contact-error">{error}</p>
                )}

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 rounded-xl bg-[#b8956b] hover:bg-[#a8855b] text-white font-sans flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t(translations.contactPage.sending)}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      {t(translations.contactPage.sendButton)}
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* FAQ Section — moved from About page (single source of truth) */}
          <section className="mt-16 pt-10 border-t border-[#c9a87c]/20" data-testid="contact-faq-section">
            <h2
              className="text-xl md:text-2xl text-[#c9a87c] mb-8"
              style={{ fontFamily: '"Patrick Hand", cursive' }}
              data-testid="contact-faq-title"
            >
              {t(translations.aboutPage.faqTitle)}
            </h2>
            <div className="space-y-6">
              {translations.aboutPage.faq.map((item, index) => (
                <div key={index} className="space-y-2" data-testid={`contact-faq-item-${index}`}>
                  <h3 className="font-serif text-[#c9a87c]/90 text-base md:text-lg font-medium">
                    {item.question[language] || item.question['en']}
                  </h3>
                  <p className="font-serif text-[#F4E6C8]/85 text-base md:text-lg leading-relaxed whitespace-pre-line">
                    {item.answer[language] || item.answer['en']}
                  </p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-8 px-6 border-t border-amber-200/10">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-amber-200/30 font-sans text-xs">
            © 2026 Nimny LLC. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
